import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useAuthStore } from '@/auth/auth-store';
import { useT } from '@/i18n/use-t';
import { useRoles } from '@/hooks/use-roles';
import {
  useCreateUser,
  useResetUserPassword,
  useUpdateUser,
  useUsers,
  type UserSummary,
} from '@/hooks/use-users';

function extractError(err: unknown): string | null {
  if (isAxiosError(err)) {
    const body = err.response?.data as { message?: string; code?: string } | undefined;
    if (body?.message) return body.code ? `${body.code}: ${body.message}` : body.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

const EMPTY = {
  email: '',
  displayName: '',
  password: '',
  isSuperAdmin: false,
  roleIds: [] as string[],
};

type EditForm = {
  email: string;
  displayName: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  roleIds: string[];
};

function toEditForm(u: UserSummary): EditForm {
  return {
    email: u.email,
    displayName: u.displayName,
    isSuperAdmin: u.isSuperAdmin,
    isActive: u.isActive,
    roleIds: u.roles.map((r) => r.id),
  };
}

export function UsersPage() {
  const t = useT();
  const { data, isLoading } = useUsers();
  const { data: roles } = useRoles();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const resetPassword = useResetUserPassword();
  const selfId = useAuthStore((s) => s.user?.id);

  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [edit, setEdit] = useState<EditForm | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaved, setEditSaved] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [passwordDone, setPasswordDone] = useState(false);

  const canSubmit =
    form.email.trim() !== '' && form.displayName.trim() !== '' && form.password.length >= 8;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCreatedEmail(null);
    try {
      const created = await createUser.mutateAsync({
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        password: form.password,
        isSuperAdmin: form.isSuperAdmin,
        roleIds: form.roleIds,
      });
      setCreatedEmail(created.email);
      setForm(EMPTY);
    } catch (err) {
      setError(extractError(err) ?? t('users.createFailed', 'Failed to create user'));
    }
  }

  function openEdit(u: UserSummary) {
    setEditing(u);
    setEdit(toEditForm(u));
    setEditError(null);
    setEditSaved(false);
    setNewPassword('');
    setPasswordDone(false);
  }

  function closeEdit() {
    setEditing(null);
    setEdit(null);
    setEditError(null);
  }

  async function saveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (editing === null || edit === null) return;
    setEditError(null);
    setEditSaved(false);
    try {
      const updated = await updateUser.mutateAsync({
        id: editing.id,
        body: {
          email: edit.email.trim(),
          displayName: edit.displayName.trim(),
          isSuperAdmin: edit.isSuperAdmin,
          isActive: edit.isActive,
          roleIds: edit.roleIds,
        },
      });
      setEditing(updated);
      setEdit(toEditForm(updated));
      setEditSaved(true);
    } catch (err) {
      setEditError(extractError(err) ?? t('users.saveFailed', 'Failed to save changes'));
    }
  }

  async function doResetPassword() {
    if (editing === null || newPassword.length < 8) return;
    setEditError(null);
    setPasswordDone(false);
    try {
      await resetPassword.mutateAsync({ id: editing.id, password: newPassword });
      setNewPassword('');
      setPasswordDone(true);
    } catch (err) {
      setEditError(extractError(err) ?? t('users.password.failed', 'Failed to reset password'));
    }
  }

  function toggleCreateRole(roleId: string) {
    setForm((f) => ({
      ...f,
      roleIds: f.roleIds.includes(roleId)
        ? f.roleIds.filter((id) => id !== roleId)
        : [...f.roleIds, roleId],
    }));
  }

  function toggleRole(roleId: string) {
    setEdit((f) =>
      f === null
        ? f
        : {
            ...f,
            roleIds: f.roleIds.includes(roleId)
              ? f.roleIds.filter((id) => id !== roleId)
              : [...f.roleIds, roleId],
          },
    );
  }

  const isSelf = editing !== null && editing.id === selfId;

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="text-lg font-semibold">{t('users.title', 'Users')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'users.subtitle',
            'Platform operators who sign in to the admin UI. Only super-admins can manage users.',
          )}
        </p>
      </header>

      {/* Create form */}
      <form onSubmit={submit} className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-primary">{t('users.new.title', 'New user')}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="email">{t('users.field.email', 'Email')}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="off"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="displayName">{t('users.field.displayName', 'Display name')}</Label>
            <Input
              id="displayName"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">{t('users.new.password', 'Initial password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              {t('users.new.passwordHint', 'Minimum 8 characters.')}
            </p>
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={form.isSuperAdmin}
                onChange={(e) => setForm((f) => ({ ...f, isSuperAdmin: e.target.checked }))}
              />
              {t('users.field.superAdmin', 'Super-admin (full access)')}
            </label>
          </div>
          <div className="space-y-1.5">
            <Label>{t('users.field.roles', 'Roles')}</Label>
            {(roles ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('users.noRoles', 'No roles defined.')}
              </p>
            ) : (
              <div className="space-y-1">
                {(roles ?? []).map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={form.roleIds.includes(r.id)}
                      onChange={() => toggleCreateRole(r.id)}
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            )}
            {form.isSuperAdmin && (
              <p className="text-xs text-muted-foreground">
                {t(
                  'users.superAdminNote',
                  'Super-admins implicitly hold every permission; roles are informational.',
                )}
              </p>
            )}
          </div>
        </div>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}
        {createdEmail !== null && (
          <p className="text-sm text-emerald-600">
            {t('users.created', 'User {email} created.').replace('{email}', createdEmail)}
          </p>
        )}

        <Button type="submit" disabled={!canSubmit || createUser.isPending}>
          {createUser.isPending
            ? t('users.new.creating', 'Creating…')
            : t('users.new.submit', 'Create user')}
        </Button>
      </form>

      {/* Edit panel */}
      {editing !== null && edit !== null && (
        <form onSubmit={saveEdit} className="space-y-4 rounded-lg border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-primary">
              {t('users.edit.title', 'Edit {email}').replace('{email}', editing.email)}
            </h2>
            <Button type="button" variant="ghost" size="sm" onClick={closeEdit}>
              {t('common.close', 'Close')}
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-email">{t('users.field.email', 'Email')}</Label>
              <Input
                id="edit-email"
                type="email"
                autoComplete="off"
                value={edit.email}
                onChange={(e) => setEdit((f) => (f === null ? f : { ...f, email: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-displayName">
                {t('users.field.displayName', 'Display name')}
              </Label>
              <Input
                id="edit-displayName"
                value={edit.displayName}
                onChange={(e) =>
                  setEdit((f) => (f === null ? f : { ...f, displayName: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={edit.isSuperAdmin}
                  disabled={isSelf}
                  onChange={(e) =>
                    setEdit((f) => (f === null ? f : { ...f, isSuperAdmin: e.target.checked }))
                  }
                />
                {t('users.field.superAdmin', 'Super-admin (full access)')}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={edit.isActive}
                  disabled={isSelf}
                  onChange={(e) =>
                    setEdit((f) => (f === null ? f : { ...f, isActive: e.target.checked }))
                  }
                />
                {t('users.field.active', 'Active (can sign in)')}
              </label>
              {isSelf && (
                <p className="text-xs text-muted-foreground">
                  {t('users.selfNote', 'You cannot deactivate or demote your own account.')}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t('users.field.roles', 'Roles')}</Label>
              {(roles ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t('users.noRoles', 'No roles defined.')}
                </p>
              ) : (
                <div className="space-y-1">
                  {(roles ?? []).map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary"
                        checked={edit.roleIds.includes(r.id)}
                        onChange={() => toggleRole(r.id)}
                      />
                      {r.name}
                    </label>
                  ))}
                </div>
              )}
              {edit.isSuperAdmin && (
                <p className="text-xs text-muted-foreground">
                  {t(
                    'users.superAdminNote',
                    'Super-admins implicitly hold every permission; roles are informational.',
                  )}
                </p>
              )}
            </div>
          </div>

          {editError !== null && <p className="text-sm text-destructive">{editError}</p>}
          {editSaved && (
            <p className="text-sm text-emerald-600">{t('users.saved', 'Changes saved.')}</p>
          )}

          <Button type="submit" disabled={updateUser.isPending}>
            {updateUser.isPending
              ? t('common.saving', 'Saving…')
              : t('users.edit.save', 'Save changes')}
          </Button>

          {/* Password reset */}
          <div className="border-t border-border pt-4">
            <h3 className="mb-2 text-sm font-semibold">
              {t('users.password.title', 'Reset password')}
            </h3>
            <div className="flex items-end gap-3">
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="edit-new-password">{t('users.password.new', 'New password')}</Label>
                <Input
                  id="edit-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={newPassword.length < 8 || resetPassword.isPending}
                onClick={() => void doResetPassword()}
              >
                {resetPassword.isPending
                  ? t('users.password.setting', 'Setting…')
                  : t('users.password.set', 'Set password')}
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(
                'users.password.hint',
                'Minimum 8 characters. The user should rotate it after signing in.',
              )}
            </p>
            {passwordDone && (
              <p className="mt-1 text-sm text-emerald-600">
                {t('users.password.done', 'Password updated.')}
              </p>
            )}
          </div>
        </form>
      )}

      {/* List */}
      <div className="rounded-lg border border-border">
        <Table>
          <THead>
            <TR>
              <TH>{t('users.field.email', 'Email')}</TH>
              <TH>{t('users.field.displayName', 'Display name')}</TH>
              <TH>{t('users.list.access', 'Access')}</TH>
              <TH>{t('users.field.roles', 'Roles')}</TH>
              <TH>{t('users.list.status', 'Status')}</TH>
              <TH>{t('users.list.created', 'Created')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={7} className="text-muted-foreground">
                  {t('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {(data?.items ?? []).map((u) => (
              <TR key={u.id} className={u.isActive ? undefined : 'opacity-60'}>
                <TD className="font-medium">{u.email}</TD>
                <TD>{u.displayName}</TD>
                <TD>
                  {u.isSuperAdmin ? (
                    <Badge>{t('users.list.superAdmin', 'Super-admin')}</Badge>
                  ) : (
                    <span className="text-muted-foreground">{t('users.list.user', 'User')}</span>
                  )}
                </TD>
                <TD className="text-muted-foreground">
                  {u.roles.length === 0 ? '—' : u.roles.map((r) => r.name).join(', ')}
                </TD>
                <TD>
                  <span className="inline-flex items-center gap-1.5 text-sm">
                    <span
                      className={`h-2 w-2 rounded-full ${
                        u.isActive ? 'bg-emerald-500' : 'bg-zinc-400'
                      }`}
                    />
                    {u.isActive
                      ? t('users.list.active', 'Active')
                      : t('users.list.inactive', 'Inactive')}
                  </span>
                </TD>
                <TD className="text-muted-foreground">
                  {new Date(u.createdAt).toLocaleDateString()}
                </TD>
                <TD>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                    {t('common.edit', 'Edit')}
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}
